import * as React from "react";
import {Button, Col, Form, ProgressBar, Row, Spinner, Table} from "react-bootstrap";
import {Ticket} from "Stores/dashboardStore/tickets";
import DatePicker from "react-datepicker";
import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    getExpandedRowModel,
    Row as RowInfo,
    useReactTable
} from "@tanstack/react-table";
import {Milestone, MilestoneStatus, MilestoneStore} from "Stores/milestoneStore";
import {inject, observer} from "mobx-react";
import {Utils} from "../../common/utils";
import {SimpleCheckbox} from "Components/Common/SimpleCheckbox";
import {HistoryStore} from "Stores/historyStore";
import {Fragment} from "react";

// const TO_DATE_FORMAT = "YYYY-MM-DD";
const TODAY: Date = new Date(Date.now());

interface ActionPlanMilestoneEditFormProps {
    actionPlan: Ticket
    milestone: Milestone
    onSubmit: (ruuid: string, status: string, description: string, dueDate: Date | undefined | null) => void
    onCancel: () => void
    milestoneStore?: MilestoneStore,
}

interface ActionPlanMilestoneEditFormState {
    dueDate?: Date | undefined | null
    description: string
    status: MilestoneStatus
    completedAt: string
    completedBy: string
}

@inject("milestoneStore")
@observer
export class ActionPlanMilestoneEditForm extends React.Component<ActionPlanMilestoneEditFormProps, ActionPlanMilestoneEditFormState> {
    constructor(props) {
        super(props);
        this.state = {
            dueDate: undefined,
            description: "",
            status: MilestoneStatus.INCOMPLETE,
            completedAt: "",
            completedBy: ""
        };
    }

    componentDidMount() {
        this.setState({
            description: this.props.milestone.description,
            dueDate: this.props.milestone.due_date,
            completedAt: this.props.milestone.completed_at ? Utils.getValidDateFromString(this.props.milestone.completed_at.toString(), "MM/DD/YY") : "",
            completedBy: this.props.milestone.completed_by_name || "",
            status: this.props.milestone.status
        });
    }

    isValid = () => {
        const description = this.state.description.trim();
        return description.length > 0;
    };

    getCompletedAt = () => {
        if (this.props.milestone.status === MilestoneStatus.COMPLETE) {
            return this.state.completedAt;
        }

        return "";
    };

    getCompletedBy = () => {
        if (this.props.milestone.status === MilestoneStatus.COMPLETE) {
            return this.state.completedBy;
        }

        return "";
    };

    handleDescriptionChange = (event: any) => {
        this.setState({description: event.target.value});
    };

    handleDueDateChange = (date: Date | null) => {
        this.setState({dueDate: date});
    };

    handleSubmit = () => {
        this.props.onCancel();
        this.props.onSubmit(
            this.props.milestone.ruuid,
            this.state.status,
            this.state.description,
            this.state.dueDate
        );
    };

    handleCancelSubmit = () => {
        this.props.onCancel();
    };

    handleStatusChange = () => {
        const status = this.state.status === MilestoneStatus.COMPLETE ? MilestoneStatus.INCOMPLETE : MilestoneStatus.COMPLETE;
        this.setState({status});
    };

    canEditMilestone = () => {
        return this.props.actionPlan.permissions.can_edit_milestones;
    };

    render() {
        if (!this.props.milestoneStore) {
            return null;
        }

        return (
            <Form>
                <Row className="mb-3">
                    <Form.Group xs={2} as={Col} controlId="formMilestoneStatus" className={"center-vertically"}>
                        <SimpleCheckbox
                            id={`actionplan-milestone-${this.props.milestone.ruuid}`}
                            isChecked={this.state.status === MilestoneStatus.COMPLETE}
                            onChange={this.handleStatusChange}
                            label={"Complete"}
                            disabled={!this.canEditMilestone()}
                        />

                    </Form.Group>
                    <Form.Group as={Col} controlId="formMilestoneDescription">
                        <Form.Label>Description</Form.Label>
                        <Form.Control
                            type="text"
                            value={this.state.description}
                            onChange={this.handleDescriptionChange}
                            disabled={!this.canEditMilestone()}
                            placeholder="Enter Description"
                            as="textarea" rows={3}/>
                    </Form.Group>

                    <Form.Group xs={3} as={Col} controlId="formMilestoneDueDate">
                        <Form.Label>Due Date</Form.Label>
                        <DatePicker
                            placeholderText="Due Date"
                            selected={this.state.dueDate}
                            disabled={!this.canEditMilestone()}
                            onChange={this.handleDueDateChange}
                            className={"form-control"}/>
                    </Form.Group>
                </Row>
                <Row>
                    <Form.Group as={Col} xs={6} controlId="formMilestoneCompletedAt">
                        <Form.Label>Completed At: {this.getCompletedAt()}</Form.Label>
                    </Form.Group>
                    <Form.Group as={Col} xs={6} controlId="formMilestoneCompletedBy">
                        <Form.Label>Completed By: {this.getCompletedBy()}</Form.Label>
                    </Form.Group>
                </Row>
                <Row className={"text-end"}>
                    <Form.Group as={Col}>
                        <Button onClick={this.handleCancelSubmit} className={"me-2"} variant={"secondary"}>Cancel</Button>
                        {this.canEditMilestone() &&
                            <Button
                                onClick={this.handleSubmit}
                                disabled={!this.isValid() || this.props.milestoneStore.isUpdatingMilestone}>
                                {this.props.milestoneStore.isUpdatingMilestone && <Spinner animation="border" className="green-spinner" size="sm"/>} Save</Button>}

                    </Form.Group>
                </Row>
            </Form>
        );
    }
}

interface MilestonesProps {
    milestoneStore?: MilestoneStore,
    historyStore?: HistoryStore,
    actionPlan: Ticket
}

interface MilestonesState {
    description: string
    dueDate?: Date | undefined | null
    expand?: {[index: number]: boolean}
    milestones: Milestone[]
}

@inject("milestoneStore", "historyStore")
@observer
export class ActionPlanMilestones extends React.Component<MilestonesProps, MilestonesState> {
    constructor(props) {
        super(props);
        this.state = {
            description: "",
            dueDate: undefined,
            expand: undefined,
            milestones: []
        };
    }

    componentDidMount() {
        if (this.props.milestoneStore) {
            this.props.milestoneStore.getAllMilestonesForActionPlan(this.props.actionPlan.ruuid).then(milestones => {
                this.setState({milestones: milestones.slice()});
            });
        }
    }

    getColumns = () => {
        const columns: ColumnDef<Milestone>[] = [
            {
                header: "Complete",
                size: 120,
                accessorKey: "status",
                cell: props => {
                    if (!this.props.milestoneStore) {
                        return null;
                    }
                    const milestone = props.row.original;
                    const handleClick = () => {
                        this.handleUpdateStatus(milestone.ruuid, milestone.status, milestone.description, milestone.due_date);
                    };

                    return (
                        <div className="text-center">{
                            this.props.milestoneStore.isUpdatingStatusMilestone ?
                                <Spinner animation="border" className="green-spinner" size="sm"/> :
                                <SimpleCheckbox
                                    id={`actionplan-milestone-${milestone.ruuid}`}
                                    isChecked={milestone.status === MilestoneStatus.COMPLETE}
                                    onChange={handleClick}
                                    disabled={!this.props.actionPlan.permissions.can_edit_milestones}
                                />}
                        </div>);
                }
            },
            {
                header: "Description",
                accessorKey: "description",
                cell: props => {
                    const milestone = props.row.original;

                    const incompleteClass = this.isIncompleteWithDueDate(milestone.status, milestone.due_date) ? "text-danger": "";
                    const className = `text-wrap ${incompleteClass}`;
                    return (<span className={className}>{milestone.description}</span>);
                }
            },
            {
                header: "Due Date",
                accessorKey: "due_date",
                cell: props => {
                    const milestone = props.row.original;

                    const className = this.isIncompleteWithDueDate(milestone.status, milestone.due_date) ? "text-danger": "";
                    return (
                        <div className="text-left">
                             <span className={className}>{milestone.due_date && Utils.formatDate(milestone.due_date, "MM/DD/YYYY")|| ""}</span>
                        </div>
                    );
                },
                size: 200
            },
            {
                id: "expander",
                header: () => null,
                size: 50,
                cell: props => {
                    return (
                        <button className="table-action-button" onClick={props.row.getToggleExpandedHandler()}>
                            <i className={`icon icon-${props.row.getIsExpanded() ? "compress" : "expand"}`}/>
                        </button>
                    );
                }
            }
        ];

        if (this.props.actionPlan.permissions.can_delete_milestones) {
            columns.push({
                id: "deleter",
                header: () => null,
                size: 50,
                // filterable: false,
                cell: props => {
                    const onClick = () => {
                        const milestone = props.row.original as Milestone;

                        this.handleDelete(milestone.ruuid);
                    };

                    return (
                        <div>
                            <button
                                className="table-action-button"
                                onClick={ onClick }>
                                <i className="icon icon-times" aria-hidden="true"/>
                            </button>
                        </div>
                    );
                }
            });
        }

        return columns;
    };

    isIncompleteWithDueDate = (status: string, dueDate: Date | undefined | null) => {
        if (!dueDate) {
            return false;
        }

        if (status === MilestoneStatus.COMPLETE) {
            return false;
        }

        return TODAY >= dueDate;
    };

    handleDescriptionChange = (event: any) => {
        this.setState({description: event.target.value});
    };

    handleDueDateChange = (date: Date | null) => {
        this.setState({dueDate: date});
    };

    isValid = () => {
        const description = this.state.description.trim();
        return description.length > 0;
    };

    handleCreate = () => {
        if (this.isValid() && this.props.milestoneStore) {
            this.props.milestoneStore.addMilestoneToActionPlan(
                this.props.actionPlan.ruuid,
                this.state.description,
                this.state.dueDate
            ).then(() => {
                this.setState({
                    description: "",
                    dueDate: undefined
                });
                if (this.props.milestoneStore) {
                    this.props.milestoneStore.getAllMilestonesForActionPlan(this.props.actionPlan.ruuid).then((milestones: Milestone[]) => {
                        this.setState({milestones: milestones.slice()});
                    });
                }
                if (this.props.historyStore) {
                    this.props.historyStore.getAllHistoryForActionPlan(this.props.actionPlan.ruuid);
                }
            });
        }
    };

    handleUpdate = (ruuid: string, status: string, description: string, dueDate: Date | undefined | null) => {
        if (this.props.milestoneStore) {
            return this.props.milestoneStore.updateMilestoneForActionPlan(this.props.actionPlan.ruuid, ruuid, status, description, dueDate).then(() => {
                if (this.props.milestoneStore) {
                    return this.props.milestoneStore.getAllMilestonesForActionPlan(this.props.actionPlan.ruuid).then((milestones: Milestone[]) => {
                        this.setState({milestones: milestones.slice()});
                    });
                }
            }).then(() => {
                this.setState({expand: undefined});
                if (this.props.historyStore) {
                    return this.props.historyStore.getAllHistoryForActionPlan(this.props.actionPlan.ruuid);
                }
            });
        }
    };

    handleUpdateStatus = (ruuid: string, status: string, description: string, dueDate: Date | undefined | null) => {
        const newStatus = status === MilestoneStatus.COMPLETE ? MilestoneStatus.INCOMPLETE : MilestoneStatus.COMPLETE;

        if (this.props.milestoneStore) {
            return this.props.milestoneStore.updateMilestoneForActionPlan(this.props.actionPlan.ruuid, ruuid, newStatus, description, dueDate).then(() => {
                if (this.props.milestoneStore) {
                    this.props.milestoneStore.getAllMilestonesForActionPlan(this.props.actionPlan.ruuid).then((milestones: Milestone[]) => {
                        this.setState({milestones: milestones.slice()});
                    });
                }
                if (this.props.historyStore) {
                    this.props.historyStore.getAllHistoryForActionPlan(this.props.actionPlan.ruuid);
                }
            }).then(() => {
                this.setState({expand: undefined});
            });
        }
    };

    handleCancelUpdate = () => {
        this.setState({expand: undefined});
    };

    handleDelete = (ruuid: string) => {
        if (this.props.milestoneStore) {
            this.props.milestoneStore.deleteMilestoneForActionPlan(this.props.actionPlan.ruuid, ruuid).then(() => {
                if (this.props.milestoneStore) {
                    this.props.milestoneStore.getAllMilestonesForActionPlan(this.props.actionPlan.ruuid).then((milestones: Milestone[]) => {
                        this.setState({milestones: milestones.slice()});
                    });
                }

                if (this.props.historyStore) {
                    this.props.historyStore.getAllHistoryForActionPlan(this.props.actionPlan.ruuid);
                }
            });
        }
    };

    getCompleteMilestonesPercentage = () => {
        if (this.state.milestones.length === 0) {
            return 0;
        }

        const total = this.state.milestones.reduce((acc, curr) => {
            if (curr.status === MilestoneStatus.COMPLETE) {
                return acc + 1;
            }
            return acc;
        }, 0);

        return Math.round(total / this.state.milestones.length * 100);
    };

    canAddMilestones = () => this.props.actionPlan.permissions.can_add_milestones;

    handleGetTrProps = (state: MilestonesState, rowInfo: RowInfo<Milestone>) => {
        if( !rowInfo ) {return {};}
        else {
            const defaultClass = "table-row-override";
            const expanded = this.state.expand;
            let cls = "";
            if( expanded && Object.keys(expanded).indexOf("" + rowInfo.index) > -1 ) {
                cls = "expanded";
            }
            cls = `${defaultClass} ${cls}`;
            return { className: cls };
        }
    };

    handleGetTdProps = () => {
        return {className: "table-data-override"};
    };

    sortMilestones = (milestones: Milestone[]) => {
        return milestones.slice().sort((milestoneA, milestoneB) => {
            if (milestoneA.status === milestoneB.status) {
                return 0;
            }

            if (milestoneA.status === MilestoneStatus.COMPLETE) {
                return 1;
            }

            return -1;
        });
    };

    subcomponent = (row: RowInfo<Milestone>) => {
        return (
            <ActionPlanMilestoneEditForm
                milestone={row.original as Milestone}
                actionPlan={this.props.actionPlan}
                onSubmit={this.handleUpdate}
                onCancel={this.handleCancelUpdate} />);
    };


    renderMilestones() {
        if (!this.props.milestoneStore) {
            return null;
        }

        const milestones = this.sortMilestones(this.state.milestones);

        return <MilestonesReactTable
                    columns={this.getColumns()}
                    data={milestones}
                    actionPlan={this.props.actionPlan}
                    handleUpdate={this.handleUpdate}/>;
    }

    getLabel = (totalPercentage: number) => {
        return (<span>{totalPercentage}% completed</span>);
    };

    render() {
        if (!this.props.milestoneStore) {
            return null;
        }
        const totalPercentage = this.getCompleteMilestonesPercentage();

        return (
            <div className="milestones-container">
                <label style={{fontWeight: "700"}}>Progress</label>
                <Row className="mb-3">
                    <Col>
                        <ProgressBar now={totalPercentage} label={this.getLabel(totalPercentage)} />
                    </Col>
                </Row>
                {this.canAddMilestones() &&
                    <Form>
                        <Row className="mb-3">
                            <Form.Group as={Col} controlId="formAddDescription">
                                <Form.Label>Description</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={this.state.description}
                                    onChange={this.handleDescriptionChange}
                                    placeholder="Enter Description" />
                            </Form.Group>

                            <Form.Group xs={3} as={Col} controlId="formAddDueDate">
                                <Form.Label>Due Date</Form.Label>
                                <DatePicker
                                    placeholderText="Due Date"
                                    minDate={TODAY}
                                    selected={ this.state.dueDate }
                                    onChange={ this.handleDueDateChange }
                                    className={"form-control"}/>
                            </Form.Group>
                            <Form.Group xs={1} as={Col} className="d-flex align-items-end">
                                <Button
                                    variant="primary"
                                    onClick={this.handleCreate}
                                    disabled={!this.isValid() || this.props.milestoneStore.isAddingMilestone}
                                >
                                    {this.props.milestoneStore.isAddingMilestone && <Spinner animation="border" className="green-spinner" size="sm"/>}
                                    Add
                                </Button>
                            </Form.Group>
                        </Row>
                    </Form>}

                {this.renderMilestones()}
            </div>
        );
    }
}

interface MilestonesReactTableProps {
    columns: ColumnDef<Milestone>[]
    data: Milestone[]
    actionPlan: Ticket
    handleUpdate: (ruuid: string, status: string, description: string, dueDate: Date | undefined | null) => void
}

function MilestonesReactTable({columns, data, actionPlan, handleUpdate}: MilestonesReactTableProps) {
    const table = useReactTable({
        columns,
        data,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        getRowCanExpand: () => true,
    });

    const renderSubComponent = (row: RowInfo<Milestone>) => {
        return (
            <ActionPlanMilestoneEditForm
                milestone={row.original}
                actionPlan={actionPlan}
                onSubmit={handleUpdate}
                onCancel={row.getToggleExpandedHandler()} />
        );
    };

    return (
        <Table>
            <thead>
            {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                        <th key={header.id}>
                            {header.isPlaceholder
                                ? null
                                : flexRender(
                                    header.column.columnDef.header,
                                    header.getContext()
                                )}
                        </th>
                    ))}
                </tr>
            ))}
            </thead>
            <tbody>
            {table.getRowModel().rows.map(row => (
                <Fragment key={row.id}>
                    <tr key={row.id}>
                        {row.getVisibleCells().map(cell => (
                            <td key={cell.id}>
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                        ))}
                    </tr>
                    {row.getIsExpanded() && (
                        <tr>
                            <td colSpan={row.getVisibleCells().length}>
                                {renderSubComponent(row)}
                            </td>
                        </tr>
                    )}
                </Fragment>
            ))}
            {table.getRowModel().rows.length === 0 && (
                <tr><td colSpan={5} className={"text-center"}>No rows found</td></tr>
            )}
            </tbody>
        </Table>
    );
}